type View = "overview" | "materials" | "channels" | "drafts" | "jobs" | "telegram";
type ReviewStatus = "candidate" | "reviewing" | "approved" | "rejected";

interface Channel {
  id: string;
  slug: string;
  peer_ref: string;
  title: string | null;
  tg_channel_id: number | null;
  enabled: boolean;
  auto_collect: boolean;
  backfill_days: number;
  include_media: boolean;
  last_synced_at: string | null;
  last_error: string | null;
}

interface Material {
  id: string;
  channel_id: string;
  channel_slug: string;
  channel_title: string | null;
  tg_message_id: number;
  excerpt: string;
  posted_at: string;
  views: number | null;
  forwards: number | null;
  reaction_count: number | null;
  score: number | null;
  score_bucket: "candidate" | "pending" | "discarded" | null;
  score_version: string | null;
  scored_at: string | null;
  review_status: ReviewStatus;
  review_note: string | null;
  has_media: boolean;
  deleted_at: string | null;
}

interface MediaAsset {
  id: number;
  status: string;
  mime: string | null;
  byte_size: number | null;
  public_url: string | null;
}

interface MaterialDetail extends Material {
  text: string;
  edited_at: string | null;
  reactions: unknown;
  media: unknown;
  score_reason: unknown;
  score_version: string | null;
  scored_at: string | null;
  media_assets: MediaAsset[];
}

interface Draft {
  id: number;
  material_id: string;
  platform: "wechat" | "xiaohongshu" | string;
  title: string;
  body: string;
  hashtags: unknown;
  source_refs: unknown;
  cta_urls: unknown;
  status: string;
  safety_status: "unchecked" | "passed" | "blocked" | string;
  safety_reason: unknown;
  version: number;
  created_at: string;
  updated_at: string;
}

interface Job {
  id: string;
  kind: string;
  material_id: string | null;
  draft_id: number | null;
  platform: string | null;
  status: string;
  attempts: number;
  error: string | null;
  created_at: string;
}

interface PublishJob {
  id: string;
  draft_id: number;
  platform: string;
  mode: string;
  draft_version: number;
  status: string;
  attempts: number;
  external_task_id: string | null;
  platform_content_id: string | null;
  canonical_url: string | null;
  error: string | null;
  created_at: string;
  updated_at: string;
}

interface FeedbackSummary {
  items: Array<{ platform: string; count: number; average_score: number | null }>;
}

interface TelegramConfig {
  configured: boolean;
  api_id: number | null;
  phone_masked: string | null;
  account_id: number | null;
  account_name: string | null;
  account_username: string | null;
  status: string;
  last_error: string | null;
  config_version: number;
  updated_at: string | null;
}

interface AppData {
  channels: Channel[];
  materials: Material[];
  drafts: Draft[];
  jobs: Job[];
  publishJobs: PublishJob[];
  feedback: FeedbackSummary;
}

const app = document.getElementById("app")!;
const tokenKey = "zimt_admin_token";
const themeKey = "zimt_theme";
type Theme = "dark" | "light";
let theme: Theme = localStorage.getItem(themeKey) === "light" ? "light" : "dark";
function applyTheme(): void { document.documentElement.dataset.theme = theme; }
applyTheme();
let currentView: View = (localStorage.getItem("zimt_view") as View | null) ?? "overview";
let data: AppData = { channels: [], materials: [], drafts: [], jobs: [], publishJobs: [], feedback: { items: [] } };
let selectedMaterial: MaterialDetail | null = null;
let materialFilters = { review: "", score: "", channel: "", query: "" };
const collectingChannels = new Map<string, string>();
let toastTimer: number | undefined;

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  opts: { class?: string; text?: string; attrs?: Record<string, string> } = {},
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (opts.class) node.className = opts.class;
  if (opts.text !== undefined) node.textContent = opts.text;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) node.setAttribute(key, value);
  return node;
}

function button(text: string, onClick: () => void | Promise<void>, className = "button secondary"): HTMLButtonElement {
  const node = el("button", { class: className, text, attrs: { type: "button" } });
  node.dataset.idleText = text;
  node.addEventListener("click", async () => {
    node.disabled = true;
    node.setAttribute("aria-busy", "true");
    node.textContent = "处理中…";
    try {
      await onClick();
    } catch (error) {
      notify((error as Error).message, "error");
    } finally {
      node.disabled = false;
      node.removeAttribute("aria-busy");
      node.textContent = node.dataset.idleText ?? text;
    }
  });
  node.addEventListener("click", (event) => event.stopPropagation());
  return node;
}

function notify(message: string, tone: "success" | "error" = "success"): void {
  let node = document.querySelector<HTMLElement>(".toast");
  if (!node) {
    node = el("div", { class: "toast" });
    document.body.appendChild(node);
  }
  node.className = `toast toast-${tone}`;
  node.textContent = message;
  if (toastTimer) window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => node?.remove(), 3600);
}

async function api<T>(path: string, init: RequestInit = {}): Promise<T> {
  const token = localStorage.getItem(tokenKey) ?? "";
  const response = await fetch(path, {
    ...init,
    headers: {
      "content-type": "application/json",
      ...(token ? { authorization: `Bearer ${token}` } : {}),
      ...(init.headers ?? {}),
    },
  });
  if (response.status === 401) {
    localStorage.removeItem(tokenKey);
    if (!document.querySelector(".auth-page")) renderLogin();
    throw new Error("登录已失效，请重新登录");
  }
  if (!response.ok) {
    let message = `请求失败（HTTP ${response.status}）`;
    try {
      const body = (await response.json()) as { message?: string };
      if (body.message) message = body.message;
    } catch {
      // 保留状态码提示。
    }
    throw new Error(message);
  }
  if (response.status === 204) return undefined as T;
  return (await response.json()) as T;
}

async function apiBlob(path: string): Promise<Blob> {
  const token = localStorage.getItem(tokenKey) ?? "";
  const response = await fetch(path, { headers: token ? { authorization: `Bearer ${token}` } : {} });
  if (!response.ok) throw new Error(`媒体读取失败（HTTP ${response.status}）`);
  return response.blob();
}

async function verifyToken(token: string): Promise<boolean> {
  if (!token.trim()) return false;
  try {
    const response = await fetch("/api/v1/channels", { headers: { authorization: `Bearer ${token.trim()}` } });
    return response.status !== 401;
  } catch {
    // 保留本地 token；短暂网络/重启故障不应强制用户重新登录。
    return true;
  }
}

function formatTime(value: string | null): string {
  if (!value) return "暂无";
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? "暂无" : date.toLocaleString("zh-CN", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" });
}

function formatNumber(value: number | null): string {
  return value == null ? "—" : new Intl.NumberFormat("zh-CN", { notation: value > 9999 ? "compact" : "standard" }).format(value);
}

function label(value: string | null | undefined): string {
  const labels: Record<string, string> = {
    candidate: "待筛选", pending: "待 AI 评分", discarded: "AI 不通过", reviewing: "审核中", approved: "已通过", rejected: "已驳回",
    enabled: "已启用", disabled: "已停用", collecting: "正在采集", waiting: "等待采集", synced: "采集完成",
    generated: "已生成", editing: "编辑中", publish_queued: "待发布", published: "已发布", failed: "失败", draft_saved: "平台草稿已保存", cancelled: "已取消",
    ready: "图片已保存", uploading: "图片保存中", pending_media: "图片待处理",
    score: "评分", generate_draft: "生成稿件", wechat: "微信公众号", xiaohongshu: "小红书",
    configured: "已配置", starting: "连接中", code_required: "等待验证码", password_required: "等待 2FA", authorized: "已授权", unconfigured: "未配置", error: "连接错误",
  };
  return value ? labels[value] ?? value : "未知";
}

function badge(value: string | null | undefined, tone?: string): HTMLElement {
  const resolved = tone ?? (value === "approved" || value === "passed" || value === "completed" || value === "published" ? "success" : value === "rejected" || value === "blocked" || value === "failed" ? "danger" : "warning");
  return el("span", { class: `badge badge-${resolved}`, text: label(value) });
}

function icon(name: string): HTMLElement {
  const paths: Record<string, string> = {
    grid: "M4 4h6v6H4zM14 4h6v6h-6zM4 14h6v6H4zM14 14h6v6h-6z",
    inbox: "M4 5h16v14H4zM4 14h4l2 3h4l2-3h4",
    radio: "M12 12h.01M8.5 8.5a5 5 0 0 0 0 7M15.5 8.5a5 5 0 0 1 0 7M5.7 5.7a9 9 0 0 0 0 12.6M18.3 5.7a9 9 0 0 1 0 12.6",
    file: "M6 3h9l4 4v14H6zM14 3v5h5M9 13h6M9 17h6",
    pulse: "M3 12h4l2-7 4 14 2-7h6",
    logout: "M10 5H5v14h5M14 8l4 4-4 4M18 12H9",
    plus: "M12 5v14M5 12h14",
    arrow: "M5 12h14M13 6l6 6-6 6",
    close: "M6 6l12 12M18 6L6 18",
  };
  const svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
  svg.setAttribute("viewBox", "0 0 24 24");
  svg.setAttribute("aria-hidden", "true");
  const path = document.createElementNS("http://www.w3.org/2000/svg", "path");
  path.setAttribute("d", paths[name] ?? paths.grid);
  svg.appendChild(path);
  return svg as unknown as HTMLElement;
}

async function loadData(): Promise<void> {
  const [channels, materials, drafts, jobs, publishJobs, feedback] = await Promise.all([
    api<Channel[]>("/api/v1/channels"),
    api<Material[]>("/api/v1/materials?limit=100"),
    api<Draft[]>("/api/v1/drafts?limit=100"),
    api<Job[]>("/api/v1/jobs"),
    api<PublishJob[]>("/api/v1/publish-jobs"),
    api<FeedbackSummary>("/api/v1/feedback/summary"),
  ]);
  data = { channels, materials, drafts, jobs, publishJobs, feedback };
}

async function login(username: string, password: string): Promise<string> {
  const response = await fetch("/api/v1/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ username: username.trim(), password }),
  });
  const payload = (await response.json().catch(() => ({}))) as { access_token?: string; message?: string };
  if (!response.ok || !payload.access_token) throw new Error(payload.message ?? "用户名或密码错误");
  return payload.access_token;
}
function renderLogin(): void {
  const page = el("main", { class: "auth-page" });
  const card = el("section", { class: "auth-card" });
  const brand = el("div", { class: "brand-lockup" });
  brand.append(el("span", { class: "brand-mark", text: "Z" }), el("span", { class: "eyebrow", text: "CONTENT OPERATIONS" }));
  card.append(brand, el("h1", { text: "回到你的内容工作台" }), el("p", { class: "auth-lead", text: "使用管理员账号登录，集中管理 Telegram 素材、AI 稿件和发布流程。" }));
  const form = el("form", { class: "auth-form" });
  const usernameField = el("label", { class: "field" });
  usernameField.append(el("span", { text: "用户名" }));
  const username = el("input", { attrs: { type: "text", autocomplete: "username", placeholder: "输入用户名", required: "required" } });
  usernameField.appendChild(username);
  const passwordField = el("label", { class: "field" });
  passwordField.append(el("span", { text: "密码" }));
  const input = el("input", { attrs: { type: "password", autocomplete: "current-password", placeholder: "输入密码", required: "required" } });
  passwordField.appendChild(input);
  const error = el("p", { class: "form-error" });
  const submit = el("button", { class: "button primary button-wide", text: "进入管理台", attrs: { type: "submit" } });
  form.append(usernameField, passwordField, error, submit);
  form.addEventListener("submit", async (event) => {
    event.preventDefault();
    error.textContent = "";
    submit.disabled = true;
    submit.textContent = "正在验证…";
    try {
      const token = await login(username.value, input.value);
      localStorage.setItem(tokenKey, token);
      await renderApp();
    } catch (err) {
      error.textContent = (err as Error).message;
    } finally {
      submit.disabled = false;
      submit.textContent = "进入管理台";
    }
  });
  card.append(form, el("p", { class: "auth-footnote", text: "密码不会保存到浏览器；登录成功后仅保存 API 会话凭证。" }));
  page.appendChild(card);
  app.replaceChildren(page);
  input.focus();
}

function renderShell(): { content: HTMLElement; shell: HTMLElement } {
  const shell = el("div", { class: "app-shell" });
  const sidebar = el("aside", { class: "sidebar" });
  const brand = el("div", { class: "sidebar-brand" });
  brand.append(el("span", { class: "brand-mark", text: "Z" }), el("span", { class: "brand-name", text: "Zimt" }));
  sidebar.appendChild(brand);
  const nav = el("nav", { class: "nav" });
  const navigation: Array<[View, string, string, Partial<typeof materialFilters>]> = [
    ["overview", "总览", "grid", {}],
    ["channels", "1 · 采集频道", "radio", {}],
    ["materials", "2 · 素材审核", "inbox", { review: "", score: "" }],
    ["materials", "3 · 已通过内容", "file", { review: "approved", score: "" }],
    ["drafts", "4 · 稿件工作流", "file", {}],
    ["jobs", "5 · 任务与反馈", "pulse", {}],
    ["telegram", "Telegram 设置", "radio", {}],
  ];
  for (const [view, text, iconName, filters] of navigation) {
    const isMaterialsAll = view === "materials" && filters.review === "" && filters.score === "";
    const isActive = currentView === view && (!view.includes("materials") || (isMaterialsAll
      ? materialFilters.review === "" && materialFilters.score === ""
      : materialFilters.review === filters.review && materialFilters.score === filters.score));
    const item = el("button", { class: `nav-item${isActive ? " active" : ""}`, attrs: { type: "button" } });
    item.append(icon(iconName), el("span", { text }));
    item.addEventListener("click", () => goTo(view, view === "materials" ? filters : undefined));
    nav.appendChild(item);
  }
  sidebar.append(nav, el("div", { class: "sidebar-footer", text: "Zimt · editorial ops" }));
  const main = el("div", { class: "shell-main" });
  const topbar = el("header", { class: "topbar" });
  const title = el("div", { class: "topbar-title" });
  title.append(el("span", { class: "status-dot" }), el("span", { text: "系统在线" }));
  const actions = el("div", { class: "topbar-actions" });
  const themeToggle = button(theme === "light" ? "切换夜间模式" : "切换日间模式", () => {
    theme = theme === "light" ? "dark" : "light";
    localStorage.setItem(themeKey, theme);
    applyTheme();
    themeToggle.dataset.idleText = theme === "light" ? "切换夜间模式" : "切换日间模式";
    themeToggle.setAttribute("aria-label", themeToggle.dataset.idleText);
  }, "button ghost");
  themeToggle.setAttribute("aria-label", themeToggle.textContent ?? "切换主题");
  actions.append(themeToggle, el("span", { class: "token-state", text: "管理员已登录" }), button("退出", () => { localStorage.removeItem(tokenKey); renderLogin(); }, "button ghost"));
  topbar.append(title, actions);
  const content = el("main", { class: "content" });
  main.append(topbar, content);
  shell.append(sidebar, main);
  app.replaceChildren(shell);
  return { content, shell };
}

function goTo(view: View, filters?: Partial<typeof materialFilters>): void {
  currentView = view;
  if (filters) materialFilters = { review: "", score: "", channel: "", query: "", ...filters };
  localStorage.setItem("zimt_view", view);
  void renderApp();
}

function pageHeader(kicker: string, title: string, description: string): HTMLElement {
  const header = el("div", { class: "page-header" });
  const copy = el("div");
  copy.append(el("span", { class: "eyebrow", text: kicker }), el("h1", { text: title }), el("p", { class: "page-description", text: description }));
  header.appendChild(copy);
  return header;
}

function metric(labelText: string, value: string, detail: string, tone: string): HTMLElement {
  const card = el("article", { class: "metric-card" });
  card.append(el("span", { class: `metric-icon metric-${tone}`, text: tone === "blue" ? "01" : tone === "green" ? "02" : tone === "orange" ? "03" : "04" }), el("span", { class: "metric-label", text: labelText }), el("strong", { class: "metric-value", text: value }), el("span", { class: "metric-detail", text: detail }));
  return card;
}

function section(title: string, description?: string): { root: HTMLElement; body: HTMLElement } {
  const root = el("section", { class: "surface" });
  const head = el("div", { class: "surface-head" });
  const copy = el("div");
  copy.appendChild(el("h2", { text: title }));
  if (description) copy.appendChild(el("p", { class: "surface-description", text: description }));
  head.appendChild(copy);
  const body = el("div", { class: "surface-body" });
  root.append(head, body);
  return { root, body };
}

function emptyState(title: string, description: string, action?: HTMLElement): HTMLElement {
  const node = el("div", { class: "empty-state" });
  node.append(el("div", { class: "empty-icon", text: "—" }), el("h3", { text: title }), el("p", { text: description }));
  if (action) node.appendChild(action);
  return node;
}

function loadingState(): HTMLElement { return el("div", { class: "loading-state", text: "正在加载工作台…" }); }

function materialRow(material: Material): HTMLElement {
  const row = el("article", { class: "material-row" });
  const main = el("div", { class: "material-main" });
  const meta = el("div", { class: "row-meta" });
  meta.append(el("span", { class: "source-label", text: material.channel_title || material.channel_slug }), el("span", { text: formatTime(material.posted_at) }), material.has_media ? el("span", { class: "media-dot", text: "媒体" }) : el("span"));
  main.append(meta, el("h3", { text: material.excerpt || "无正文内容" }));
  const stats = el("div", { class: "row-stats" });
  stats.append(el("span", { text: `阅读 ${formatNumber(material.views)}` }), el("span", { text: `转发 ${formatNumber(material.forwards)}` }), el("span", { text: `反应 ${formatNumber(material.reaction_count)}` }));
  const actions = el("div", { class: "row-actions material-actions" });
  const status = contentStatus(material);
  actions.append(badge(status.value, status.tone));
  if (material.score_bucket) actions.append(badge(`AI：${label(material.score_bucket)}`, material.score_bucket === "candidate" ? "success" : material.score_bucket === "discarded" ? "danger" : "warning"));
  const draftPlatforms = data.drafts.filter((draft) => draft.material_id === material.id).map((draft) => label(draft.platform));
  if (draftPlatforms.length) actions.append(badge(`稿件 ${draftPlatforms.join("、")}`, "muted"));
  actions.append(button("查看详情", () => openMaterial(material.id), "button ghost"));
  row.addEventListener("click", () => { void openMaterial(material.id); });
  row.setAttribute("tabindex", "0");
  row.setAttribute("role", "button");
  row.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void openMaterial(material.id); } });
  row.append(main, stats, actions);
  return row;
}

function materialTable(materials: Material[]): HTMLElement {
  const wrapper = el("div", { class: "material-table-wrap" });
  const table = document.createElement("table");
  table.className = "material-table";
  table.setAttribute("aria-label", "素材完整信息");
  const head = document.createElement("thead");
  const headRow = document.createElement("tr");
  ["来源 / 消息", "内容摘要", "发布时间", "阅读", "转发", "反应", "AI 分数", "AI 状态", "人工审核", "媒体", "稿件", "操作"].forEach((text) => headRow.appendChild(el("th", { text })));
  head.appendChild(headRow);
  table.appendChild(head);
  const body = document.createElement("tbody");
  for (const material of materials) {
    const row = document.createElement("tr");
    row.className = "material-table-row";
    row.setAttribute("tabindex", "0");
    row.setAttribute("role", "button");
    const drafts = data.drafts.filter((draft) => draft.material_id === material.id);
    const source = el("td", { class: "material-table-source" });
    source.append(el("strong", { text: material.channel_title || material.channel_slug }), el("span", { text: `#${material.tg_message_id}` }));
    const cells: HTMLElement[] = [
      source,
      el("td", { class: "material-table-excerpt", text: material.excerpt || "无正文内容" }),
      el("td", { text: formatTime(material.posted_at) }),
      el("td", { text: formatNumber(material.views) }),
      el("td", { text: formatNumber(material.forwards) }),
      el("td", { text: formatNumber(material.reaction_count) }),
      el("td", { class: "material-table-score", text: material.score == null ? "—" : `${(material.score * 100).toFixed(0)}%` }),
      el("td"),
      el("td"),
      el("td", { text: material.has_media ? "有图片" : "无图片" }),
      el("td", { class: drafts.length ? "material-table-drafts" : "material-table-pending", text: drafts.length ? drafts.map((draft) => label(draft.platform)).join("、") : "待生成" }),
      el("td"),
    ];
    cells[7].appendChild(badge(material.score_bucket ?? "pending", material.score_bucket === "candidate" ? "success" : material.score_bucket === "discarded" ? "danger" : "warning"));
    const status = contentStatus(material);
    cells[8].appendChild(badge(status.value, status.tone));
    const detail = button("详情", () => openMaterial(material.id), "button ghost");
    detail.addEventListener("click", (event) => event.stopPropagation());
    cells[11].appendChild(detail);
    row.append(...cells);
    row.addEventListener("click", () => { void openMaterial(material.id); });
    row.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void openMaterial(material.id); } });
    body.appendChild(row);
  }
  table.appendChild(body);
  wrapper.appendChild(table);
  return wrapper;
}

async function openMaterial(id: string): Promise<void> {
  selectedMaterial = await api<MaterialDetail>(`/api/v1/materials/${id}`);
  selectedMaterial.media_assets = await api<MediaAsset[]>(`/api/v1/materials/${id}/assets`);
  renderDrawer();
}

async function createDraftJob(materialId: string, platform: "wechat" | "xiaohongshu"): Promise<void> {
  await api(`/api/v1/materials/${materialId}/drafts`, { method: "POST", body: JSON.stringify({ platform }) });
  notify(`${label(platform)}生成任务已创建`);
  await loadData();
  goTo("drafts");
}

async function queueScore(materialId: string): Promise<void> {
  await api(`/api/v1/materials/${materialId}/score`, { method: "POST" });
  notify("评分任务已加入队列");
  await loadData();
  await renderApp();
}

function scoreReasonText(value: unknown): string[] {
  if (Array.isArray(value)) return value.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
  if (value && typeof value === "object") {
    const reason = (value as { reason?: unknown }).reason;
    if (Array.isArray(reason)) return reason.filter((item): item is string => typeof item === "string" && item.trim().length > 0);
    if (typeof reason === "string" && reason.trim()) return [reason];
    const rule = (value as { rule?: unknown }).rule;
    if (typeof rule === "string" && rule.trim()) return [`规则：${rule}`];
  }
  return [];
}

function contentStatus(material: Material): { value: string; tone: string } {
  if (material.deleted_at) return { value: "已删除", tone: "danger" };
  if (material.review_status === "rejected") return { value: "已驳回", tone: "danger" };
  if (material.review_status === "approved") return { value: "已通过审核", tone: "success" };
  if (material.review_status === "reviewing") return { value: "审核中", tone: "warning" };
  if (material.score_bucket === "discarded") return { value: "AI 不通过", tone: "muted" };
  if (material.score_bucket === "candidate") return { value: "待人工审核", tone: "warning" };
  return { value: "待 AI 评分", tone: "warning" };
}

function aiDecision(bucket: Material["score_bucket"]): { title: string; tone: string } {
  if (bucket === "candidate") return { title: "AI 审核通过", tone: "success" };
  if (bucket === "discarded") return { title: "AI 审核不通过", tone: "danger" };
  if (bucket === "pending") return { title: "AI 审核待处理", tone: "warning" };
  return { title: "等待 AI 审核", tone: "muted" };
}

function renderDrawer(): void {
  document.querySelector(".drawer-backdrop")?.remove();
  if (!selectedMaterial) return;
  const backdrop = el("div", { class: "drawer-backdrop" });
  const drawer = el("aside", { class: "drawer" });
  const head = el("div", { class: "drawer-head" });
  head.append(el("div", { class: "eyebrow", text: "MATERIAL DETAIL" }), button("关闭", () => { selectedMaterial = null; renderDrawer(); }, "icon-button"));
  const title = el("h2", { text: selectedMaterial.channel_title || selectedMaterial.channel_slug });
  const meta = el("p", { class: "surface-description", text: `消息 #${selectedMaterial.tg_message_id} · ${formatTime(selectedMaterial.posted_at)}` });
  const body = el("div", { class: "drawer-content" });
  body.append(title, meta);
  const badges = el("div", { class: "badge-row" });
  badges.append(badge(contentStatus(selectedMaterial).value, contentStatus(selectedMaterial).tone));
  body.appendChild(badges);
  const decision = aiDecision(selectedMaterial.score_bucket);
  const aiPanel = el("div", { class: `ai-review ai-review-${decision.tone}` });
  const aiHead = el("div", { class: "ai-review-head" });
  aiHead.append(el("span", { class: "eyebrow", text: "AI 审核" }), el("strong", { text: decision.title }));
  if (selectedMaterial.score != null) aiHead.appendChild(el("span", { class: "ai-score", text: `评分 ${(selectedMaterial.score * 100).toFixed(0)}%` }));
  aiPanel.appendChild(aiHead);
  const reasons = scoreReasonText(selectedMaterial.score_reason);
  if (reasons.length > 0) {
    const reasonList = el("ul", { class: "ai-reasons" });
    reasons.forEach((reason) => reasonList.appendChild(el("li", { text: reason })));
    aiPanel.appendChild(reasonList);
  } else {
    aiPanel.appendChild(el("p", { class: "muted", text: selectedMaterial.score_version ? "AI 未返回具体原因" : "等待 AI 评分结果" }));
  }
  const stats = el("div", { class: "detail-stats" });
  stats.append(metric("阅读", formatNumber(selectedMaterial.views), "Telegram views", "blue"), metric("转发", formatNumber(selectedMaterial.forwards), "Telegram forwards", "green"), metric("反应", formatNumber(selectedMaterial.reaction_count), "Reactions", "orange"));
  body.appendChild(stats);
  const original = el("div", { class: "original-copy" });
  original.append(el("span", { class: "eyebrow", text: "原始正文" }), el("p", { text: selectedMaterial.text || "（无正文）" }));
  body.appendChild(original);
  if (selectedMaterial.media_assets.length > 0) {
    const mediaPanel = el("div", { class: "media-gallery" });
    mediaPanel.appendChild(el("span", { class: "eyebrow", text: "采集图片" }));
    for (const asset of selectedMaterial.media_assets) {
      const item = el("div", { class: "media-item" });
      if (asset.status === "ready") {
        item.classList.add("media-loading");
        const image = el("img", { attrs: { alt: "Telegram 图片", loading: "lazy" } });
        void apiBlob(`/api/v1/assets/${asset.id}`).then((blob) => {
          image.src = URL.createObjectURL(blob);
          item.classList.remove("media-loading");
        }).catch(() => {
          item.classList.remove("media-loading");
          item.appendChild(el("span", { class: "muted", text: "图片读取失败" }));
        });
        item.appendChild(image);
      } else {
        item.appendChild(el("span", { class: "muted", text: `图片${label(asset.status)}` }));
      }
      mediaPanel.appendChild(item);
    }
    body.appendChild(mediaPanel);
  }
  body.appendChild(aiPanel);
  if (selectedMaterial.review_note) body.appendChild(el("p", { class: "review-note-display", text: `上次人工备注：${selectedMaterial.review_note}` }));
  const note = el("textarea", { class: "review-note", attrs: { rows: "3", placeholder: "审核备注（可选）：记录为什么通过或驳回" } });
  const reviewActions = el("div", { class: "drawer-actions" });
  if (selectedMaterial.review_status !== "approved" && selectedMaterial.review_status !== "rejected") {
    reviewActions.append(button("通过审核", async () => { await reviewMaterial(selectedMaterial!.id, "approved", note.value); selectedMaterial = null; renderDrawer(); }, "button primary"), button("驳回素材", async () => { await reviewMaterial(selectedMaterial!.id, "rejected", note.value); selectedMaterial = null; renderDrawer(); }, "button danger"));
  } else {
    reviewActions.append(badge(selectedMaterial.review_status, selectedMaterial.review_status === "approved" ? "success" : "danger"));
  }
  body.append(el("span", { class: "eyebrow", text: "人工审核" }), note, reviewActions);
  const workflow = el("div", { class: "drawer-actions drawer-secondary-actions" });
  if (selectedMaterial.review_status === "approved") {
    workflow.append(button("生成微信稿件", () => createDraftJob(selectedMaterial!.id, "wechat"), "button secondary"), button("生成小红书稿件", () => createDraftJob(selectedMaterial!.id, "xiaohongshu"), "button secondary"));
  } else {
    workflow.append(el("p", { class: "surface-description", text: "人工通过后才能生成平台稿件" }));
  }
  if (selectedMaterial.score_bucket !== "candidate") workflow.append(button("重新评分", () => queueScore(selectedMaterial!.id), "button ghost"));
  body.append(workflow);
  drawer.append(head, body);
  backdrop.appendChild(drawer);
  backdrop.addEventListener("click", (event) => { if (event.target === backdrop) { selectedMaterial = null; renderDrawer(); } });
  const closeOnEscape = (event: KeyboardEvent) => { if (event.key === "Escape") { document.removeEventListener("keydown", closeOnEscape); selectedMaterial = null; renderDrawer(); } };
  document.addEventListener("keydown", closeOnEscape);
  document.body.appendChild(backdrop);
}

async function reviewMaterial(id: string, status: ReviewStatus, note?: string): Promise<void> {
  await api(`/api/v1/materials/${id}/review`, { method: "PATCH", body: JSON.stringify({ status, note: note?.trim() || null, actor: "web-admin" }) });
  await loadData();
  notify(status === "approved" ? "素材已通过审核" : "素材已驳回");
  await renderApp();
}

function renderTelegram(content: HTMLElement): void {
  content.appendChild(pageHeader("TELEGRAM CONTROL", "Telegram 设置", "凭据只保存在后台加密存储；验证码和 2FA 密码只在登录请求期间传递，不会落库。"));
  const configPanel = section("账号配置", "配置 Telegram API 后，Collector 会在后台等待登录指令。");
  const form = el("form", { class: "editor-form" });
  const apiId = el("input", { attrs: { type: "number", min: "1", placeholder: "例如 123456" } });
  const apiHash = el("input", { attrs: { type: "password", autocomplete: "new-password", placeholder: "Telegram API Hash" } });
  const phone = el("input", { attrs: { type: "tel", placeholder: "+8613812345678" } });
  for (const [title, input] of [["API ID", apiId], ["API Hash", apiHash], ["手机号", phone]] as Array<[string, HTMLInputElement]>) {
    const field = el("label", { class: "field" }); field.append(el("span", { text: title }), input); form.appendChild(field);
  }
  const actions = el("div", { class: "form-actions" });
  actions.appendChild(button("保存配置", async () => {
    await api<TelegramConfig>("/api/v1/telegram/config", { method: "PUT", body: JSON.stringify({ api_id: Number(apiId.value), api_hash: apiHash.value, phone: phone.value }) });
    notify("Telegram 配置已保存"); await renderApp();
  }, "button primary"));
  form.appendChild(actions); configPanel.body.appendChild(form); content.appendChild(configPanel.root);

  const authPanel = section("登录状态", "登录成功后 session 会持久化在 Collector 的 Docker volume 中。");
  const statusText = el("div", { class: "telegram-status-table", text: "正在读取状态…" });
  const authActions = el("div", { class: "form-actions" });
  const code = el("input", { attrs: { type: "text", inputmode: "numeric", placeholder: "Telegram 验证码" } });
  const password = el("input", { attrs: { type: "password", autocomplete: "one-time-code", placeholder: "2FA 密码" } });
  const start = button("开始登录", async () => { await api("/api/v1/telegram/auth/start", { method: "POST", body: "{}" }); notify("登录流程已启动"); await refreshTelegram(statusText); }, "button primary");
  const submitCode = button("提交验证码", async () => { await api("/api/v1/telegram/auth/code", { method: "POST", body: JSON.stringify({ code: code.value }) }); code.value = ""; await refreshTelegram(statusText); }, "button secondary");
  const submitPassword = button("提交 2FA", async () => { await api("/api/v1/telegram/auth/password", { method: "POST", body: JSON.stringify({ password: password.value }) }); password.value = ""; await refreshTelegram(statusText); }, "button secondary");
  const logout = button("退出 Telegram", async () => { await api("/api/v1/telegram/auth/logout", { method: "POST", body: "{}" }); notify("已请求退出 Telegram"); await refreshTelegram(statusText); }, "button danger");
  authActions.append(start, submitCode, submitPassword, logout);
  authPanel.body.append(statusText, code, password, authActions); content.appendChild(authPanel.root);
  void refreshTelegram(statusText);
  const timer = window.setInterval(() => { if (document.body.contains(statusText)) void refreshTelegram(statusText); else window.clearInterval(timer); }, 3000);
}

async function refreshTelegram(target: HTMLElement): Promise<void> {
  const config = await api<TelegramConfig>("/api/v1/telegram/config");
  const table = document.createElement("table");
  table.className = "status-table";
  const body = document.createElement("tbody");
  const rows: Array<[string, string, HTMLElement?]> = [
    ["连接状态", label(config.status), badge(config.status)],
    ["账号名称", config.account_name || "尚未读取"],
    ["用户名", config.account_username ? `@${config.account_username}` : "—"],
    ["账号 ID", config.account_id == null ? "—" : String(config.account_id)],
    ["手机号", config.phone_masked || "—"],
    ["配置更新时间", formatTime(config.updated_at)],
    ["错误信息", config.last_error || "—"],
  ];
  for (const [name, value, content] of rows) {
    const row = document.createElement("tr");
    row.append(el("th", { text: name }));
    const cell = document.createElement("td");
    cell.appendChild(content ?? el("span", { text: value }));
    row.appendChild(cell);
    body.appendChild(row);
  }
  table.appendChild(body);
  target.replaceChildren(table);
}

function renderOverview(content: HTMLElement): void {
  content.appendChild(pageHeader("WORKSPACE / OVERVIEW", "内容运营总览", "从采集到审核、从稿件到发布，今天的工作集中在这里。"));
  const candidates = data.materials.filter((item) => item.score_bucket === "candidate").length;
  const pendingAi = data.materials.filter((item) => item.score_bucket === "pending" || item.score_bucket == null).length;
  const review = data.materials.filter((item) => item.review_status === "candidate" || item.review_status === "reviewing").length;
  const active = data.channels.filter((item) => item.enabled).length;
  const running = data.jobs.filter((item) => item.status === "queued" || item.status === "running").length;
  const metrics = el("div", { class: "metrics-grid" });
  metrics.append(metric("待人工审核", String(review), "打开审核队列", "orange"), metric("AI 候选", String(candidates), "优先处理", "green"), metric("待 AI 评分", String(pendingAi), "等待处理", "purple"), metric("启用频道", String(active), `${running} 个任务处理中`, "blue"));
  metrics.querySelectorAll<HTMLElement>(".metric-card").forEach((card, index) => {
    card.setAttribute("role", "button");
    card.setAttribute("tabindex", "0");
    card.addEventListener("click", () => {
      if (index === 0) goTo("materials", { review: "candidate", score: "" });
      else if (index === 1) goTo("materials", { review: "", score: "candidate" });
      else if (index === 2) goTo("materials", { review: "", score: "pending" });
      else goTo("channels");
    });
    card.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); card.click(); } });
  });
  content.appendChild(metrics);
  const grid = el("div", { class: "content-grid" });
  const recent = section("最近采集", "按 Telegram 发布时间排序");
  if (!data.materials.length) recent.body.appendChild(emptyState("还没有素材", "先到采集频道选择数量并手工采集；原文和图片会完整保留。", button("去采集频道", () => goTo("channels"), "button primary")));
  else data.materials.slice(0, 5).forEach((item) => recent.body.appendChild(materialRow(item)));
  const flow = section("工作流状态", "当前系统的处理队列");
  const flowItems: Array<[string, string, string]> = [["采集频道", `${active} 个启用`, "success"], ["素材审核", `${review} 条待处理`, review ? "warning" : "success"], ["AI 任务", `${running} 个运行中`, running ? "warning" : "muted"], ["稿件", `${data.drafts.length} 条稿件`, data.drafts.length ? "success" : "muted"]];
  for (const [name, value, tone] of flowItems) {
    const item = el("div", { class: "flow-item" });
    item.append(el("span", { class: `flow-dot flow-${tone}` }), el("span", { text: name }), el("strong", { text: value }));
    flow.body.appendChild(item);
  }
  const quick = el("div", { class: "quick-actions" });
  quick.append(button("去采集", () => goTo("channels"), "button primary"), button("处理待审核", () => goTo("materials", { review: "candidate" }), "button secondary"), button("查看稿件", () => goTo("drafts"), "button secondary"));
  flow.body.appendChild(quick);
  grid.append(recent.root, flow.root);
  content.appendChild(grid);
}

function renderMaterials(content: HTMLElement): void {
  content.appendChild(pageHeader("INBOX / REVIEW", "素材审核", "先处理待人工审核；已通过的内容会保留在“已通过内容”菜单中，不会消失。原文和图片始终保留。"));
  const queueBar = el("div", { class: "queue-bar" });
  const queues: Array<[string, string, Partial<typeof materialFilters>]> = [
    ["全部", "", { review: "", score: "" }],
    ["待人工", String(data.materials.filter((item) => item.review_status === "candidate" || item.review_status === "reviewing").length), { review: "candidate", score: "" }],
    ["AI 候选", String(data.materials.filter((item) => item.score_bucket === "candidate").length), { review: "", score: "candidate" }],
    ["待评分", String(data.materials.filter((item) => item.score_bucket === "pending" || item.score_bucket == null).length), { review: "", score: "pending" }],
    ["AI 不通过", String(data.materials.filter((item) => item.score_bucket === "discarded").length), { review: "", score: "discarded" }],
    ["已通过内容", String(data.materials.filter((item) => item.review_status === "approved").length), { review: "approved", score: "" }],
  ];
  queues.forEach(([name, count, filters]) => {
    const item = el("button", { class: `queue-chip${materialFilters.review === filters.review && materialFilters.score === filters.score ? " active" : ""}`, attrs: { type: "button" } });
    item.append(el("span", { text: name }), el("strong", { text: count || "0" }));
    item.addEventListener("click", () => { materialFilters = { ...materialFilters, ...filters, channel: "", query: "" }; void renderApp(); });
    queueBar.appendChild(item);
  });
  content.appendChild(queueBar);
  const toolbar = el("form", { class: "filter-bar" });
  const statusSelect = el("select", { attrs: { name: "review_status" } });
  [["", "全部审核状态"], ["candidate", "待筛选"], ["reviewing", "审核中"], ["approved", "已通过"], ["rejected", "已驳回"]].forEach(([value, text]) => statusSelect.appendChild(el("option", { text, attrs: { value } })));
  const scoreSelect = el("select", { attrs: { name: "score_bucket" } });
  [["", "全部评分桶"], ["candidate", "AI 候选"], ["pending", "待 AI 评分"], ["discarded", "AI 不通过"]].forEach(([value, text]) => scoreSelect.appendChild(el("option", { text, attrs: { value } })));
  const channelSelect = el("select", { attrs: { name: "channel_id" } });
  channelSelect.appendChild(el("option", { text: "全部频道", attrs: { value: "" } }));
  data.channels.forEach((item) => channelSelect.appendChild(el("option", { text: item.title || item.peer_ref, attrs: { value: item.id } })));
  const search = el("input", { attrs: { type: "search", placeholder: "按正文快速筛选…", name: "search" } });
  statusSelect.value = materialFilters.review;
  scoreSelect.value = materialFilters.score;
  channelSelect.value = materialFilters.channel;
  search.value = materialFilters.query;
  const clear = button("清除筛选", () => { materialFilters = { review: "", score: "", channel: "", query: "" }; statusSelect.value = ""; scoreSelect.value = ""; channelSelect.value = ""; search.value = ""; draw(); }, "button ghost");
  toolbar.append(statusSelect, scoreSelect, channelSelect, search, el("button", { class: "button secondary", text: "应用筛选", attrs: { type: "submit" } }), clear);
  const list = el("div", { class: "material-table-host" });
  const resultCount = el("p", { class: "result-count" });
  const draw = () => {
    list.replaceChildren();
    const review = statusSelect.value;
    const score = scoreSelect.value;
    const channel = channelSelect.value;
    const query = search.value.trim().toLowerCase();
    materialFilters = { review, score, channel, query };
    const filtered = data.materials.filter((item) => (!review || item.review_status === review) && (!score || item.score_bucket === score) && (!channel || item.channel_id === channel) && (!query || item.excerpt.toLowerCase().includes(query)));
    resultCount.textContent = `当前显示 ${filtered.length} 条 · 共 ${data.materials.length} 条`;
    if (!filtered.length) list.appendChild(emptyState("没有匹配素材", "调整筛选条件，或等待 Collector 采集新的内容。"));
    else list.appendChild(materialTable(filtered));
  };
  toolbar.addEventListener("submit", (event) => { event.preventDefault(); draw(); });
  [statusSelect, scoreSelect, channelSelect].forEach((control) => control.addEventListener("change", draw));
  search.addEventListener("input", draw);
  draw();
  const panel = section("素材队列", `${data.materials.length} 条素材 · 表格展示完整状态，点击行查看正文、图片和审核动作`);
  panel.body.append(resultCount, toolbar, list);
  content.appendChild(panel.root);
}

function renderChannels(content: HTMLElement): void {
  content.appendChild(pageHeader("SOURCES / TELEGRAM", "采集频道", "先启用频道，再选择 1、5 或 10 条手工采集。重复采集会自动去重，原文和图片会完整保留。"));
  const grid = el("div", { class: "channel-grid" });
  for (const channel of data.channels) {
    const card = el("article", { class: "channel-card" });
  const collectionStage = collectingChannels.get(channel.id);
    const collectionStatus = collectionStage ? { value: "collecting", tone: "warning" } : channel.last_error ? { value: "failed", tone: "danger" } : channel.last_synced_at ? { value: "synced", tone: "success" } : { value: "waiting", tone: "muted" };
    const head = el("div", { class: "card-head" });
    head.append(el("div", { class: "channel-avatar", text: (channel.title || channel.peer_ref).slice(0, 1).toUpperCase() }), el("div", { class: "card-title", text: channel.title || channel.peer_ref }), badge(channel.enabled ? "enabled" : "disabled", channel.enabled ? "success" : "muted"), badge(collectionStatus.value, collectionStatus.tone));
    card.appendChild(head);
    card.append(el("code", { text: channel.peer_ref }), el("p", { class: "card-description", text: `${channel.auto_collect ? "自动采集已开启" : "仅手工采集"} · ${channel.include_media ? "会保存图片" : "仅保存文字"}` }));
    if (collectionStage) card.appendChild(el("div", { class: "inline-progress", text: collectionStage }));
    if (channel.last_error) card.appendChild(el("p", { class: "form-error", text: channel.last_error }));
    const meta = el("div", { class: "card-meta" });
    meta.append(el("span", { text: `上次同步 ${formatTime(channel.last_synced_at)}` }), el("span", { text: channel.tg_channel_id ? `ID ${channel.tg_channel_id}` : "等待解析" }));
    card.appendChild(meta);
    const actions = el("div", { class: "card-actions" });
    const manualLimit = el("select", { class: "manual-limit", attrs: { "aria-label": "手工采集条数" } });
    [["1", "1 条"], ["5", "5 条"], ["10", "10 条"]].forEach(([value, text]) => manualLimit.appendChild(el("option", { text, attrs: { value } })));
    actions.append(
      button(channel.enabled ? "停用" : "启用", async () => { await api(`/api/v1/channels/${channel.id}`, { method: "PATCH", body: JSON.stringify({ enabled: !channel.enabled }) }); await loadData(); await renderApp(); }, "button ghost"),
      button(channel.auto_collect ? "关闭自动采集" : "开启自动采集", async () => {
        if (!channel.auto_collect && !window.confirm("开启后 Collector 会自动读取该频道，确定继续吗？")) return;
        await api(`/api/v1/channels/${channel.id}`, { method: "PATCH", body: JSON.stringify({ auto_collect: !channel.auto_collect }) });
        notify(channel.auto_collect ? "已关闭自动采集" : "已开启自动采集"); await loadData(); await renderApp();
      }, "button ghost"),
      manualLimit,
      button(collectionStage ? "采集中…" : "开始手工采集", async () => {
        collectingChannels.set(channel.id, "正在从 Telegram 读取消息…");
        try {
          const result = await api<{ messages?: number; media_failures?: number }>(`/api/v1/channels/${channel.id}/collect`, { method: "POST", body: JSON.stringify({ max_messages: Number(manualLimit.value) }) });
          collectingChannels.set(channel.id, "正在更新内容状态…");
          const mediaFailures = result.media_failures ?? 0;
          notify(mediaFailures > 0
            ? `采集完成：文字已保存 ${result.messages ?? 0} 条，但有 ${mediaFailures} 张图片失败`
            : `采集完成：已保存 ${result.messages ?? 0} 条内容，图片正常`);
          await loadData();
          collectingChannels.delete(channel.id);
          await renderApp();
        } finally { collectingChannels.delete(channel.id); }
      }, "button primary"),
      button("删除", async () => { if (!window.confirm(`确定删除 ${channel.peer_ref}？`)) return; await api(`/api/v1/channels/${channel.id}`, { method: "DELETE" }); await loadData(); notify("频道已删除"); await renderApp(); }, "button danger"),
    );
    manualLimit.disabled = !channel.enabled || Boolean(collectionStage);
    const actionButtons = actions.querySelectorAll<HTMLButtonElement>("button");
    const collectButton = actionButtons[2];
    if (collectButton) collectButton.disabled = !channel.enabled || Boolean(collectionStage);
    card.appendChild(actions);
    grid.appendChild(card);
  }
  const add = section("添加频道", "支持 @username、公开 t.me 链接或数字频道 ID");
  const form = el("form", { class: "channel-form" });
  const peer = el("input", { attrs: { name: "peer_ref", placeholder: "@channel_name", required: "required" } });
  const title = el("input", { attrs: { name: "title", placeholder: "备注名称（可选）" } });
  const days = el("input", { attrs: { name: "backfill_days", type: "number", min: "0", max: "365", value: "7" } });
  const media = el("input", { attrs: { name: "include_media", type: "checkbox" } });
  media.checked = true;
  form.append(field("频道地址", peer), field("备注", title), field("回溯天数", days), field("采集媒体", media), el("button", { class: "button primary", text: "添加频道", attrs: { type: "submit" } }));
  form.addEventListener("submit", async (event) => { event.preventDefault(); const submit = form.querySelector<HTMLButtonElement>("button[type=submit]")!; submit.disabled = true; try { await api("/api/v1/channels", { method: "POST", body: JSON.stringify({ peer_ref: peer.value.trim(), title: title.value.trim() || null, backfill_days: Number(days.value) || 0, include_media: media.checked }) }); notify("频道已添加"); await loadData(); await renderApp(); } catch (error) { notify((error as Error).message, "error"); } finally { submit.disabled = false; } });
  add.body.appendChild(form);
  content.append(grid, add.root);
}

function field(labelText: string, control: HTMLElement): HTMLElement {
  const wrapper = el("label", { class: "field" });
  wrapper.append(el("span", { text: labelText }), control);
  return wrapper;
}

async function editDraft(draft: Draft): Promise<void> {
  document.querySelector(".draft-editor-backdrop")?.remove();
  const backdrop = el("div", { class: "drawer-backdrop draft-editor-backdrop" });
  const panel = el("section", { class: "draft-editor" });
  const head = el("div", { class: "drawer-head" });
  head.append(el("div", { class: "eyebrow", text: "DRAFT EDITOR" }), button("关闭", () => backdrop.remove(), "icon-button"));
  const title = el("input", { attrs: { value: draft.title, placeholder: "稿件标题" } });
  const body = el("textarea", { attrs: { rows: "18", placeholder: "稿件正文" } });
  body.value = draft.body;
  const error = el("p", { class: "form-error" });
  const save = button("保存修改", async () => {
    error.textContent = "";
    await api(`/api/v1/drafts/${draft.id}`, { method: "PATCH", body: JSON.stringify({ title: title.value, body: body.value }) });
    backdrop.remove();
    notify("稿件已保存");
    await loadData();
    await renderApp();
  }, "button primary");
  panel.append(head, el("h2", { text: draft.title || "编辑稿件" }), field("标题", title), field("正文", body), error, save);
  backdrop.appendChild(panel);
  backdrop.addEventListener("click", (event) => { if (event.target === backdrop) backdrop.remove(); });
  document.body.appendChild(backdrop);
  title.focus();
}
function pendingDraftTable(materials: Material[]): HTMLElement {
  const wrapper = el("div", { class: "draft-table-wrap" });
  const table = document.createElement("table");
  table.className = "draft-table pending-draft-table";
  table.setAttribute("aria-label", "待生成稿件信息");
  const head = document.createElement("thead");
  const headRow = document.createElement("tr");
  ["来源 / 消息", "内容摘要", "发布时间", "审核状态", "媒体", "微信稿", "小红书稿", "操作"].forEach((text) => headRow.appendChild(el("th", { text })));
  head.appendChild(headRow);
  table.appendChild(head);
  const body = document.createElement("tbody");
  for (const material of materials) {
    const row = document.createElement("tr");
    row.className = "draft-table-row";
    row.setAttribute("role", "button");
    row.setAttribute("tabindex", "0");
    const existingPlatforms = new Set(data.drafts.filter((draft) => draft.material_id === material.id).map((draft) => draft.platform));
    const hasPendingJob = (platform: string) => data.jobs.some((job) => job.material_id === material.id && job.platform === platform && (job.status === "queued" || job.status === "running"));
    const source = el("td", { class: "draft-table-source" });
    source.append(el("strong", { text: material.channel_title || material.channel_slug }), el("span", { text: `#${material.tg_message_id}` }));
    const actions = el("td", { class: "draft-table-actions" });
    actions.append(button("查看素材", () => openMaterial(material.id), "button ghost"));
    actions.addEventListener("click", (event) => event.stopPropagation());
    const platformCell = (platform: "wechat" | "xiaohongshu") => {
      const cell = el("td");
      if (existingPlatforms.has(platform)) cell.appendChild(badge("已生成", "success"));
      else if (hasPendingJob(platform)) cell.appendChild(badge("生成中", "warning"));
      else cell.appendChild(button(`生成${label(platform)}`, () => createDraftJob(material.id, platform), "button secondary"));
      cell.addEventListener("click", (event) => event.stopPropagation());
      return cell;
    };
    const status = contentStatus(material);
    row.append(
      source,
      el("td", { class: "draft-table-copy", text: material.excerpt || "无正文内容" }),
      el("td", { text: formatTime(material.posted_at) }),
      (() => { const cell = el("td"); cell.appendChild(badge(status.value, status.tone)); return cell; })(),
      el("td", { text: material.has_media ? "有图片" : "无图片" }),
      platformCell("wechat"),
      platformCell("xiaohongshu"),
      actions,
    );
    row.addEventListener("click", () => { void openMaterial(material.id); });
    row.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void openMaterial(material.id); } });
    body.appendChild(row);
  }
  table.appendChild(body);
  wrapper.appendChild(table);
  return wrapper;
}

function draftTable(drafts: Draft[]): HTMLElement {
  const wrapper = el("div", { class: "draft-table-wrap" });
  const table = document.createElement("table");
  table.className = "draft-table";
  table.setAttribute("aria-label", "稿件完整信息");
  const head = document.createElement("thead");
  const headRow = document.createElement("tr");
  ["稿件标题 / 正文摘要", "来源素材", "平台", "稿件状态", "安全状态", "版本", "更新时间", "发布状态", "操作"].forEach((text) => headRow.appendChild(el("th", { text })));
  head.appendChild(headRow);
  table.appendChild(head);
  const body = document.createElement("tbody");
  for (const draft of drafts) {
    const row = document.createElement("tr");
    row.className = "draft-table-row";
    row.setAttribute("role", "button");
    row.setAttribute("tabindex", "0");
    const material = data.materials.find((item) => item.id === draft.material_id);
    const publishJob = data.publishJobs.find((job) => job.draft_id === draft.id);
    const title = el("td", { class: "draft-table-copy" });
    title.append(el("strong", { text: draft.title || "未命名稿件" }), el("span", { text: draft.body.slice(0, 180) || "（正文为空）" }));
    const source = el("td", { class: "draft-table-source", text: material?.excerpt || `素材 ${draft.material_id.slice(0, 8)}` });
    const actions = el("td", { class: "draft-table-actions" });
    actions.append(button("查看 / 编辑", () => editDraft(draft), "button ghost"));
    if (draft.safety_status === "passed" && draft.status !== "published") actions.append(button("通过审核", async () => { await api(`/api/v1/drafts/${draft.id}/review`, { method: "POST", body: JSON.stringify({ status: "approved", actor: "web-admin" }) }); notify("稿件已通过审核"); await loadData(); await renderApp(); }, "button primary"));
    if (draft.status === "approved") actions.append(button("保存平台草稿", async () => { await api(`/api/v1/drafts/${draft.id}/publish`, { method: "POST", body: JSON.stringify({ mode: "draft_only" }) }); notify("平台草稿任务已创建，等待 SyncBlog 领取"); await loadData(); await renderApp(); }, "button secondary"));
    actions.addEventListener("click", (event) => event.stopPropagation());
    const cells: HTMLElement[] = [
      title,
      source,
      el("td", { class: "draft-table-platform", text: label(draft.platform) }),
      el("td"),
      el("td"),
      el("td", { text: `第 ${draft.version} 版` }),
      el("td", { text: formatTime(draft.updated_at) }),
      el("td"),
      actions,
    ];
    cells[3].appendChild(badge(draft.status));
    cells[4].appendChild(badge(draft.safety_status));
    if (publishJob) cells[7].appendChild(badge(publishJob.status, publishJob.status === "draft_saved" ? "success" : publishJob.status === "failed" ? "danger" : "warning"));
    else cells[7].appendChild(el("span", { class: "muted", text: "未发布" }));
    row.append(...cells);
    row.addEventListener("click", () => { void editDraft(draft); });
    row.addEventListener("keydown", (event) => { if (event.key === "Enter" || event.key === " ") { event.preventDefault(); void editDraft(draft); } });
    body.appendChild(row);
  }
  table.appendChild(body);
  wrapper.appendChild(table);
  return wrapper;
}

function renderDrafts(content: HTMLElement): void {
  const header = pageHeader("EDITORIAL / DRAFTS", "稿件工作流", "审核通过的内容会先进入待生成区，再进入稿件编辑和平台草稿，不会在流程中消失。");
  header.appendChild(button("刷新生成状态", async () => { await loadData(); await renderApp(); }, "button ghost"));
  content.appendChild(header);
  const approved = data.materials.filter((material) => material.review_status === "approved");
  const pendingMaterials = approved.filter((material) => {
    const platforms = new Set(data.drafts.filter((draft) => draft.material_id === material.id).map((draft) => draft.platform));
    return !platforms.has("wechat") || !platforms.has("xiaohongshu");
  });
  const pendingPanel = section("待生成稿件", `${pendingMaterials.length} 条已通过内容`);
  if (!pendingMaterials.length) {
    pendingPanel.body.appendChild(emptyState("没有待生成内容", approved.length ? "已通过内容都已进入稿件列表。" : "先在素材审核中通过内容，才能生成平台稿件。"));
  } else {
    pendingPanel.body.appendChild(pendingDraftTable(pendingMaterials));
  }
  content.appendChild(pendingPanel.root);

  const panel = section("稿件列表", `${data.drafts.length} 条稿件 · 点击任意稿件查看完整生成效果`);
  if (!data.drafts.length) panel.body.appendChild(emptyState("还没有稿件", "从上方待生成区选择平台，创建 AI 稿件生成任务。"));
  else panel.body.appendChild(draftTable(data.drafts));
  content.appendChild(panel.root);
}

async function openTaskTarget(materialId: string | null, draftId: number | null): Promise<void> {
  selectedMaterial = null;
  if (draftId != null) {
    currentView = "drafts";
    localStorage.setItem("zimt_view", currentView);
    await renderApp();
    const draft = data.drafts.find((item) => item.id === draftId);
    if (draft) await editDraft(draft);
    return;
  }
  if (materialId) {
    currentView = "materials";
    materialFilters = { review: "", score: "", channel: "", query: "" };
    localStorage.setItem("zimt_view", currentView);
    await renderApp();
    await openMaterial(materialId);
    return;
  }
  notify("这个任务没有关联的素材或稿件", "error");
}

function makeTaskClickable(row: HTMLElement, materialId: string | null, draftId: number | null): void {
  row.classList.add("job-row-clickable");
  row.setAttribute("role", "link");
  row.setAttribute("tabindex", "0");
  row.addEventListener("click", () => { void openTaskTarget(materialId, draftId); });
  row.addEventListener("keydown", (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      void openTaskTarget(materialId, draftId);
    }
  });
}

function renderJobs(content: HTMLElement): void {
  content.appendChild(pageHeader("SYSTEM / OBSERVABILITY", "任务与反馈", "查看 AI 队列和发布后的反馈汇总，快速发现卡住的环节。"));
  const grid = el("div", { class: "content-grid" });
  const jobsPanel = section("AI 任务队列", `${data.jobs.length} 条最近任务`);
  if (!data.jobs.length) jobsPanel.body.appendChild(emptyState("队列为空", "创建评分或稿件生成任务后会显示在这里。"));
  else for (const job of data.jobs) {
    const row = el("div", { class: "job-row" });
    const copy = el("div");
    copy.append(el("strong", { text: label(job.kind) }), el("span", { class: "job-id", text: job.id.slice(0, 8) }));
    if (job.error) copy.appendChild(el("p", { class: "form-error", text: job.error }));
    row.append(copy, badge(job.status), el("span", { class: "job-time", text: formatTime(job.created_at) }));
    makeTaskClickable(row, job.material_id, job.draft_id);
    jobsPanel.body.appendChild(row);
  }
  const publishHeading = el("div", { class: "subsection-heading", text: "发布任务" });
  jobsPanel.body.appendChild(publishHeading);
  if (!data.publishJobs.length) jobsPanel.body.appendChild(el("p", { class: "surface-description", text: "还没有进入 SyncBlog bridge 的发布任务。" }));
  else for (const job of data.publishJobs.slice(0, 8)) {
    const row = el("div", { class: "job-row" });
    const copy = el("div");
    copy.append(el("strong", { text: `${label(job.platform)} · ${label(job.mode)}` }), el("span", { class: "job-id", text: job.id.slice(0, 8) }));
    if (job.error) copy.appendChild(el("p", { class: "form-error", text: job.error }));
    row.append(copy, badge(job.status), el("span", { class: "job-time", text: formatTime(job.updated_at) }));
    makeTaskClickable(row, null, job.draft_id);
    jobsPanel.body.appendChild(row);
  }
  const feedbackPanel = section("发布反馈", "平台反馈事件汇总");
  if (!data.feedback.items.length) feedbackPanel.body.appendChild(emptyState("暂无反馈", "真实平台发布并回传反馈后，统计会出现在这里。"));
  else for (const item of data.feedback.items) {
    const row = el("div", { class: "feedback-row" });
    row.append(el("strong", { text: label(item.platform) }), el("span", { text: `${item.count} 条事件` }), el("b", { text: item.average_score == null ? "—" : item.average_score.toFixed(2) }));
    feedbackPanel.body.appendChild(row);
  }
  grid.append(jobsPanel.root, feedbackPanel.root);
  content.appendChild(grid);
}

async function renderApp(): Promise<void> {
  const token = localStorage.getItem(tokenKey) ?? "";
  if (!(await verifyToken(token))) {
    localStorage.removeItem(tokenKey);
    renderLogin();
    return;
  }
  const { content } = renderShell();
  content.appendChild(loadingState());
  try {
    await loadData();
    content.replaceChildren();
    if (currentView === "overview") renderOverview(content);
    else if (currentView === "materials") renderMaterials(content);
    else if (currentView === "channels") renderChannels(content);
    else if (currentView === "drafts") renderDrafts(content);
    else if (currentView === "jobs") renderJobs(content);
    else renderTelegram(content);
  } catch (error) {
    content.replaceChildren(emptyState("工作台暂时无法加载", (error as Error).message, button("重试", () => renderApp(), "button primary")));
  }
}

void (localStorage.getItem(tokenKey) ? renderApp() : Promise.resolve(renderLogin()));
